import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  PenTool,
  Search,
  ChevronLeft,
  CheckSquare,
  ListOrdered,
  Type,
  Mic,
  MousePointer,
  Loader2,
  Play
} from 'lucide-react'

const exerciseTypeIcons: Record<string, React.ElementType> = {
  multiple_choice: CheckSquare,
  fill_blank: Type,
  matching: MousePointer,
  ordering: ListOrdered,
  listening: Mic,
  writing: PenTool,
  speaking: Mic,
  translation: Type
}

const exerciseTypeLabels: Record<string, string> = {
  multiple_choice: 'Opción múltiple',
  fill_blank: 'Completar espacios',
  matching: 'Emparejar',
  ordering: 'Ordenar',
  listening: 'Escucha',
  writing: 'Escritura',
  speaking: 'Expresión oral',
  translation: 'Traducción'
}

export default function ExercisesList() {
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [exercises, setExercises] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      const { data } = await supabase
        .from('exercises')
        .select('*, content:contents(id, title, lesson_id, lesson:lessons(id, title, unit_id))')
        .eq('is_active', true)
        .order('order_num')
      if (data) setExercises(data)
      setLoading(false)
    }

    loadData()
  }, [])

  const filteredExercises = exercises.filter(ex => {
    const matchesSearch = (ex.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = typeFilter === 'all' || ex.exercise_type === typeFilter
    return matchesSearch && matchesType
  })

  const exerciseTypes = [...new Set(exercises.map(e => e.exercise_type).filter(Boolean))]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/teacher/content" className="p-2 hover:bg-muted rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Ejercicios</h1>
          <p className="text-muted-foreground">
            {`${exercises.length} ejercicios disponibles (solo lectura)`}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg bg-background"
        >
          <option value="all">Todos los tipos</option>
          {exerciseTypes.map(type => (
            <option key={type} value={type}>
              {exerciseTypeLabels[type] || type}
            </option>
          ))}
        </select>
      </div>

      {/* Lista */}
      <div className="grid gap-3">
        {filteredExercises.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border">
            <PenTool className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium">No se encontraron ejercicios</p>
          </div>
        ) : (
          filteredExercises.map((exercise) => {
            const TypeIcon = exerciseTypeIcons[exercise.exercise_type] || PenTool
            const typeLabel = exerciseTypeLabels[exercise.exercise_type] || exercise.exercise_type

            return (
              <div
                key={exercise.id}
                className="bg-card rounded-xl border p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <TypeIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{exercise.title}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="px-2 py-0.5 bg-primary/10 text-primary rounded">{typeLabel}</span>
                      {exercise.skill && (
                        <span className="px-2 py-0.5 bg-muted rounded">{exercise.skill}</span>
                      )}
                      {exercise.points && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">{exercise.points} pts</span>
                      )}
                      {exercise.content?.lesson?.title && (
                        <span className="px-2 py-0.5 bg-muted rounded">{exercise.content.lesson.title}</span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/teacher/preview/exercise/${exercise.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors shrink-0"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Ver
                  </Link>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
